// src/context/CartContext.js
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { cartItemTotal, cartItemUnitPrice } from '../utils/quantityTiers';

const CartContext = createContext(null);

const resolveDealId = (productOrId) => {
  if (!productOrId) return null;
  if (typeof productOrId === "string") return productOrId;

  // si product.dealId est un objet
  if (productOrId.dealId && typeof productOrId.dealId === "object") {
    return productOrId.dealId._id || productOrId.dealId.id || null;
  }

  // si product.dealId est une string
  if (productOrId.dealId && typeof productOrId.dealId === "string") {
    return productOrId.dealId;
  }

  // fallback
  return productOrId._id || productOrId.id || productOrId.slug || null;
};

const normalizeCartItem = (product, quantity = 1, idx = 0) => {
  const dealId = resolveDealId(product);
  const qty = Math.max(1, Math.floor(Number(quantity) || 1));
  const price = Number(product?.price ?? product?.discountedPrice ?? 0) || 0;
  const variantLabel = product?.variantLabel || null;

  return {
    _localKey: product?._localKey || `${dealId || "x"}${variantLabel ? `-${variantLabel}` : ""}-${idx}`,
    dealId, // ✅ stable pour Checkout
    quantity: qty,
    price,
    // Prix degressif : on garde le prix de base ET les paliers sur la ligne.
    // Sans eux, le panier ne peut pas recalculer quand la quantite change —
    // c'est ce qui faisait payer le prix plein malgre la remise annoncee.
    basePrice: price,
    quantityTiers: Array.isArray(product?.quantityTiers) ? product.quantityTiers : [],
    merchantId:
      (product?.merchantId && typeof product.merchantId === "object"
        ? product.merchantId.id || product.merchantId._id
        : product?.merchantId) || null, // ✅ requis pour les codes promo marchand
    title: product?.title || (product?.dealId && product.dealId.title) || "Deal",
    image:
      product?.image ||
      (Array.isArray(product?.images) && product.images[0]) ||
      (product?.dealId && product.dealId.images && product.dealId.images[0]) ||
      "",
    _id: product?._id || dealId, // compat
    // Marketplace multi-types : variante choisie + infos produit
    variantLabel,
    productType: product?.productType || null,
    shippingFee: Number(product?.shippingFee) || 0,
    freeShippingThreshold:
      (product?.freeShippingThreshold != null && product?.freeShippingThreshold !== "")
        ? Number(product.freeShippingThreshold) : null,
    shippingZones: Array.isArray(product?.shippingZones) ? product.shippingZones : [],
    // Lot D — tarif au poids
    shippingMode: product?.shippingMode === "weight" ? "weight" : "flat",
    weightGrams: Number(product?.weightGrams) || 0,
    shippingWeightTiers: Array.isArray(product?.shippingWeightTiers) ? product.shippingWeightTiers : [],
  };
};

export const CartProvider = ({ children }) => {
  const mountedRef = useRef(true);
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage once
  useEffect(() => {
    mountedRef.current = true;
    try {
      const savedCart = localStorage.getItem("cart");
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed)) {
          const normalized = parsed
            .map((it, idx) => normalizeCartItem(it, it?.quantity ?? 1, idx))
            .filter((it) => it.dealId);
          setCartItems(normalized);
        }
      }
    } catch (e) {
      // ignore
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Persist cart to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cartItems));
    } catch (e) {
      // ignore
    }
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    const dealId = resolveDealId(product);
    if (!dealId) return;

    const qty = Math.max(1, Math.floor(Number(quantity) || 1));

    // Conversion marketing : ajout au panier (Meta/GA/TikTok).
    try {
      if (window.mxTrack) window.mxTrack('AddToCart', {
        value: (Number(product?.price ?? product?.discountedPrice ?? 0) || 0) * qty,
        currency: 'MAD',
        content_name: product?.title || product?.name || ''
      });
    } catch (e) {}

    setCartItems((prev) => {
      const variantLabel = product?.variantLabel || null;
      const sameLine = (item) =>
        String(item.dealId) === String(dealId) && (item.variantLabel || null) === variantLabel;
      const existing = prev.find(sameLine);
      if (existing) {
        // On ne se contente PAS d'augmenter la quantite : la ligne deja presente
        // peut etre un vieux snapshot (panier repris du localStorage, ou ajoute
        // avant que le marchand ne cree ses paliers). Elle n'aurait alors jamais
        // ni `basePrice` ni `quantityTiers`, et le prix degressif resterait
        // invisible tant que le client ne viderait pas son panier.
        // On rafraichit donc les champs qui viennent de l'annonce, en gardant la
        // quantite cumulee et la cle de ligne.
        // Rafraichissement CIBLE, pas un `...fresh` global : selon l'ecran
        // d'origine (fiche produit ou carte de liste), l'objet `product` ne
        // porte pas toujours les frais de port ni le type de produit — les
        // ecraser en bloc effacerait des donnees valides de la ligne existante.
        const hasTiers = product && Object.prototype.hasOwnProperty.call(product, 'quantityTiers');
        const freshPrice = Number(product?.price ?? product?.discountedPrice ?? 0) || 0;

        return prev.map((item) => {
          if (!sameLine(item)) return item;
          const next = { ...item, quantity: (Number(item.quantity) || 0) + qty };
          // Prix de base : indispensable pour barrer le prix plein et pour
          // calculer la remise. Absent des lignes d'avant le prix degressif.
          if (next.basePrice == null) next.basePrice = Number(item.price) || freshPrice;
          // Paliers : on suit l'annonce telle qu'elle est MAINTENANT, y compris
          // si le marchand les a supprimes — sinon on afficherait une remise
          // que le serveur ne facturerait plus.
          if (hasTiers) {
            next.quantityTiers = Array.isArray(product.quantityTiers) ? product.quantityTiers : [];
          } else if (!Array.isArray(next.quantityTiers)) {
            next.quantityTiers = [];
          }
          return next;
        });
      }
      const newItem = normalizeCartItem(product, qty, prev.length);
      return [...prev, newItem];
    });
  };

  // Identifie une LIGNE de panier de façon unique (variante incluse).
  // Reproduit la logique de _localKey pour matcher la bonne ligne.
  const lineKeyOf = (item) => {
    if (item && item._localKey) return item._localKey;
    const d = resolveDealId(item);
    const vl = item && item.variantLabel ? `-${item.variantLabel}` : "";
    return `${d || "x"}${vl}`;
  };

  // target = objet ligne (avec _localKey/variantLabel) -> match exact ligne (variante incluse)
  //        = dealId (string) -> compat ascendante : retire toutes les lignes du deal
  const removeFromCart = (target) => {
    if (target && typeof target === "object") {
      const key = lineKeyOf(target);
      setCartItems((prev) => prev.filter((item) => lineKeyOf(item) !== key));
      return;
    }
    const dealId = resolveDealId(target);
    if (!dealId) return;
    setCartItems((prev) => prev.filter((item) => String(item.dealId) !== String(dealId)));
  };

  const updateQuantity = (target, quantity) => {
    const qty = Math.floor(Number(quantity) || 0);

    if (target && typeof target === "object") {
      const key = lineKeyOf(target);
      setCartItems((prev) =>
        qty <= 0
          ? prev.filter((item) => lineKeyOf(item) !== key)
          : prev.map((item) => (lineKeyOf(item) === key ? { ...item, quantity: qty } : item))
      );
      return;
    }

    const dealId = resolveDealId(target);
    if (!dealId) return;
    setCartItems((prev) =>
      qty <= 0
        ? prev.filter((item) => String(item.dealId) !== String(dealId))
        : prev.map((item) =>
            String(item.dealId) === String(dealId) ? { ...item, quantity: qty } : item
          )
    );
  };

  const clearCart = () => setCartItems([]);

  const cartCount = useMemo(
    () => cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0),
    [cartItems]
  );

  const cartTotal = useMemo(
    () =>
      cartItems.reduce((acc, item) => acc + cartItemTotal(item), 0),
    [cartItems]
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart doit être utilisé à l'intérieur de CartProvider");
  return ctx;
};
